import {
    type CellContext as TanStackCellContext,
    type ColumnVisibilityState,
    type ColumnDef as TanStackColumnDef,
    type Row as TanStackRow,
    type RowData,
    columnFilteringFeature,
    columnResizingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    createExpandedRowModel,
    createFilteredRowModel,
    createPaginatedRowModel,
    createSortedRowModel,
    globalFilteringFeature,
    rowExpandingFeature,
    rowPaginationFeature,
    rowSortingFeature,
    sortFn_basic,
    tableFeatures
} from '@tanstack/react-table';

/**
 * Shared TanStack Table v9 features used by DataTable and ExpandableDataTable.
 * Keeping this static also gives every consumer one stable feature type for
 * column, row, and cell definitions.
 */
export const dataTableFeatures = tableFeatures({
    columnFilteringFeature,
    globalFilteringFeature,
    filteredRowModel: createFilteredRowModel(),
    rowSortingFeature,
    sortedRowModel: createSortedRowModel(),
    sortFns: { basic: sortFn_basic },
    rowPaginationFeature,
    paginatedRowModel: createPaginatedRowModel(),
    rowExpandingFeature,
    expandedRowModel: createExpandedRowModel(),
    columnVisibilityFeature,
    columnSizingFeature,
    columnResizingFeature
});

/** Presentation hints DataTable reads from `columnDef.meta`. */
export interface DataTableColumnMeta {
    headerClassName?: string;
    className?: string;
    /** Pins the column to an edge of the scroll area so it stays in view while the other columns scroll. */
    sticky?: 'left' | 'right';
    /**
     * Distance from the pinned edge in px. Omit it to stack the column against the pinned columns
     * between it and that edge, using their rendered widths.
     */
    stickyOffset?: number;
}

export type DataTableFeatures = typeof dataTableFeatures;
export type ColumnDef<TData extends RowData, TValue = unknown> = TanStackColumnDef<DataTableFeatures, TData, TValue>;
export type Row<TData extends RowData> = TanStackRow<DataTableFeatures, TData>;
export type CellContext<TData extends RowData, TValue = unknown> = TanStackCellContext<DataTableFeatures, TData, TValue>;
export type VisibilityState = ColumnVisibilityState;

export type { ExpandedState, RowData, SortingState, Updater } from '@tanstack/react-table';
